import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { freeProduct } from '@lib/productsState/productsTypes';
import { CartState, ProductCartItem } from './types';
import { ProductVariantItem } from '../productsState/productsTypes';
// helpers
import {
  recalcProductsQuantity,
  updateExistingCartItem,
  addProductToCart,
  addFreeProductToCart,
  addBundleProductToCart,
  isEligibleForFreeItem,
  getEligibleFreeItemsForProduct,
} from '@utils/cartHelpers';

const initialState: CartState = {
  loader: false,
  isCartOpen: false,
  isHeroVisible: false,
  finalPrice: 0,
  freeProductStep: 3,
  country: window.country,
  cartProducts: [],
  warrantyProduct: {
    enabled: window.productData.add_warranty_for_product,
    warrantyState: window.productData.warranty_preselect ? true : false,
    preselect: window.productData.warranty_preselect || false,
    price: Number(window.productData.Warranty_Price) || 0,
    konnektive_id: window.productData.warranty_product_konnektive_id || '',
    sku: window.productData.warranty_product_sku || '',
    generic_name: window.productData.warranty_product_name || '',
  },
  shipping: {
    freeShippingApplied: false,
    shippingPrice: window.productData.shipping_price || 0,
  },
  paidItemsCount: 0,
  freeProductsCount: 0,
  productsQuantity: 0,
  totalSavings: 0,
  subTotal: 0,
  freeShippingPrice: Number(window.productData?.shopping_cart?.tr_shopping_cart_amount_of_free_shipping_unlock) || 0,
  loadLazyComponents: false,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    toggleCart: (state) => {
      state.isCartOpen = !state.isCartOpen;
    },
    loadLazy: (state) => {
      state.loadLazyComponents = true;
    },
    isHeroVisible: (state) => {
      state.isHeroVisible = !state.isHeroVisible;
    },
    setLoader: (state) => {
      state.loader = !state.loader;
    },
    toggleWarranty: (state, action: PayloadAction<boolean>) => {
      state.warrantyProduct.warrantyState = action.payload;
    },
    setFreeShipping: (state, action: PayloadAction<boolean>) => {
      state.shipping.freeShippingApplied = action.payload;
    },
    setTotalSavings: (state) => {
      const total = 0;
      state.totalSavings = total;
    },
    setFinalPrice: (state, action: PayloadAction<{ total: number; savings: number }>) => {
      state.finalPrice = action.payload.total;
      state.totalSavings = action.payload.savings;
    },
    setProductsQuantity: (state) => {
      state.productsQuantity = recalcProductsQuantity(state.cartProducts);
    },
    setSavingsQuantityPrice: (state) => {
      cartSlice.caseReducers.setTotalSavings(state);
      cartSlice.caseReducers.setProductsQuantity(state);
    },
    setCartProducts: (state, action: PayloadAction<ProductCartItem[]>) => {
      state.cartProducts = action.payload;
      state.productsQuantity = recalcProductsQuantity(state.cartProducts);
    },
    addItem: (state, action: PayloadAction<ProductCartItem>) => {
      const product = action.payload;

      const productId = product.id;
      const indexOfProduct = state.cartProducts.findIndex((item) => item.id === productId);
      if (indexOfProduct !== -1) {
        const prevAmount = Number(state.cartProducts[indexOfProduct].amount);
        state.cartProducts[indexOfProduct].amount = Number(prevAmount) + Number(product.amount);
        if (product.variants) {
          if (state.cartProducts[indexOfProduct].variants) {
            state.cartProducts[indexOfProduct].variants = [
              ...state.cartProducts[indexOfProduct].variants,
              ...product.variants,
            ];
          } else {
            state.cartProducts[indexOfProduct].variants = [...product.variants];
          }
        }
      } else {
        state.cartProducts.push(product);
      }

      state.productsQuantity = recalcProductsQuantity(state.cartProducts);
    },
    addMainProduct: (state, action: PayloadAction<ProductCartItem>) => {
      const product = action.payload;

      let variants = product?.variants || [];

      const freeProductsWithVariants =
        (product.free_products && product.free_products?.filter((item: freeProduct) => item.apply_variants === true)) ||
        [];

      for (const freeProduct of freeProductsWithVariants) {
        const freeProductId = `${product.id}_free`;
        const variantsToApply = freeProduct.apply_variants ? variants.slice(0, freeProduct.fp_amount) : [];
        variants = variants.slice(freeProduct.fp_amount);

        if (!updateExistingCartItem(state.cartProducts, freeProductId, freeProduct.fp_amount, variantsToApply)) {
          addFreeProductToCart(state.cartProducts, product, freeProduct, variantsToApply);
        }
      }

      const variantsToApply = variants.slice(0, product.amount) ?? [];
      variants = variants.slice(product.amount);

      if (!updateExistingCartItem(state.cartProducts, product.id, product.amount, variantsToApply)) {
        addBundleProductToCart(state.cartProducts, product, variantsToApply);
      }

      state.productsQuantity = recalcProductsQuantity(state.cartProducts);
    },
    removeItem: (state, action: PayloadAction<{ id: string; variant?: ProductVariantItem | null }>) => {
      const { id, variant } = action.payload;
      const product = state.cartProducts.find((p) => p.id === id);
      if (!product) return;

      const freeProductStep = Number(product.free_product_step);
      const freeProductId = `${id}_free`;

      state.cartProducts = state.cartProducts.map((p) => {
        if (p.id === id && variant) {
          const updatedVariants = p.variants?.filter((v) => v.variantId !== variant.variantId) ?? [];
          return {
            ...p,
            variants: updatedVariants,
            amount: updatedVariants.length,
          };
        }
        return p;
      });

      const updatedProduct = state.cartProducts.find((p) => p.id === id);
      if (!updatedProduct) {
        state.cartProducts = state.cartProducts.filter((p) => p.id !== freeProductId);
        state.productsQuantity = recalcProductsQuantity(state.cartProducts);
        return;
      }

      const remainingPaidItems = updatedProduct.amount || 0;
      const shouldBeFreeItems = freeProductStep ? Math.floor(remainingPaidItems / (freeProductStep - 1)) : 0;

      const freeProductIndex = state.cartProducts.findIndex((p) => p.id === freeProductId);
      if (shouldBeFreeItems === 0) {
        state.cartProducts = state.cartProducts.filter((p) => p.id !== freeProductId);
      } else if (freeProductIndex !== -1) {
        const freeProduct = state.cartProducts[freeProductIndex];
        let updatedVariants = [...(freeProduct.variants || [])];
        if (variant) {
          updatedVariants = updatedVariants.filter((v) => !(v.color === variant.color && v.size === variant.size));
        }
        if (updatedVariants.length > shouldBeFreeItems) {
          updatedVariants = updatedVariants.slice(0, shouldBeFreeItems);
        }
        state.cartProducts[freeProductIndex] = {
          ...freeProduct,
          variants: updatedVariants,
          amount: shouldBeFreeItems,
        };
      }
      state.cartProducts = state.cartProducts.filter((p) => (p.amount || 0) > 0);
      state.productsQuantity = recalcProductsQuantity(state.cartProducts);
    },

    incrementItem: (
      state,
      action: PayloadAction<{
        id: string;
        variant?: ProductVariantItem | null;
        price: number;
        oldPrice: number;
      }>
    ) => {
      const { id, variant } = action.payload;
      const product = state.cartProducts.find((product) => product.id === id);
      const freeProductStep = product?.free_product_step;
      const isEligible =
        typeof freeProductStep === 'number' ? isEligibleForFreeItem(state.cartProducts, id, freeProductStep) : false;
      const freeProductId = `${id}_free`;
      const productId = !isEligible ? id.replace('_free', '') : freeProductId;

      const productInArray = state.cartProducts.find((product) => product.id === productId);

      if (!productInArray && product) {
        addProductToCart(state.cartProducts, product, [variant]);
      } else {
        state.cartProducts = state.cartProducts.map((product) => {
          if (product.id === productId) {
            product.amount = Number(product.amount) + 1;
            if (variant) {
              product.variants?.push(variant);
            }
          }
          return product;
        });
      }
    },
    decrementItem: (
      state,
      action: PayloadAction<{
        id: string;
        variant?: ProductVariantItem | null;
        price: number;
        oldPrice: number;
      }>
    ) => {
      const { id, variant } = action.payload;
      const product = state.cartProducts.find((product) => product.id === id);

      if (!product) return;

      const remainingPaidItems = (product.amount || 0) - 1; // After decrement

      const shouldBeFreeItems = Math.floor(remainingPaidItems / (Number(product?.free_product_step) - 1));

      const currentFreeProducts = state.cartProducts.find((product) => product.id === `${id}_free`)?.amount || 0;

      const shouldRemoveFreeProduct = currentFreeProducts > shouldBeFreeItems;
      const productId = id;

      state.cartProducts = state.cartProducts
        .map((product) => {
          if (shouldRemoveFreeProduct && product.id === `${id}_free`) {
            product.amount = Number(product.amount) - 1;
            if (variant && product.variants) {
              const variantIndex = product.variants.findIndex((v) => v.variantId === variant.variantId);
              if (variantIndex !== -1) {
                product.variants = [
                  ...product.variants.slice(0, variantIndex),
                  ...product.variants.slice(variantIndex + 1),
                ];
              }
            }
          } else if (product.id === productId) {
            product.amount = Number(product.amount) - 1;
            if (variant && product.variants) {
              const variantIndex = product.variants.findIndex((v) => v.variantId === variant.variantId);
              if (variantIndex !== -1) {
                product.variants = [
                  ...product.variants.slice(0, variantIndex),
                  ...product.variants.slice(variantIndex + 1),
                ];
              }
            }
          }
          return product;
        })
        .filter((product) => product.amount > 0);

      state.productsQuantity = recalcProductsQuantity(state.cartProducts);
    },
    setSubtotalPrice: (state, action: PayloadAction<{ subTotal: number }>) => {
      state.subTotal = action.payload.subTotal;
    },
  },
});

export const {
  toggleCart,
  toggleWarranty,
  loadLazy,
  isHeroVisible,
  setLoader,
  setTotalSavings,
  setFinalPrice,
  setProductsQuantity,
  setSavingsQuantityPrice,
  setCartProducts,
  addMainProduct,
  addItem,
  removeItem,
  setFreeShipping,
  setSubtotalPrice,
  incrementItem,
  decrementItem,
} = cartSlice.actions;

export default cartSlice.reducer;
